import React, { useState } from 'react';
import {
  Clock,
  ArrowRight,
  Database,
  MessageSquare,
  Trash2,
  Check,
  X,
  Download,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const WORKSPACE_CARD_GRID =
  'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

const formatCreatedAt = value => {
  if (!value) return 'Unknown';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const WorkspaceCard = ({
  id,
  name,
  sources,
  createdAt,
  onPreview,
  onOpenWorkspace,
  onDelete,
  chatsCount,
  showExportJson,
  onExportJson,
  exportJsonLoading,
  isLocked = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = e => {
    e.stopPropagation();
    setIsDeleting(true);
  };

  const cancelDelete = e => {
    e.stopPropagation();
    setIsDeleting(false);
  };

  const confirmDelete = e => {
    e.stopPropagation();
    onDelete(id);
    setIsDeleting(false);
  };

  return (
    <div
      onClick={() => !isDeleting && !isLocked && onPreview(id)}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-5 text-left shadow-sm transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 dark:border-slate-800 dark:bg-slate-900',
        isLocked
          ? 'cursor-not-allowed opacity-70'
          : 'cursor-pointer hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-lg hover:shadow-slate-200/70 dark:hover:border-emerald-800/70 dark:hover:shadow-black/20'
      )}
    >
      <div className="mb-3 flex w-full items-start justify-between gap-2">
        <div className="min-w-0">
          <h3
            className={cn(
              'line-clamp-2 text-[13px] font-semibold leading-5 tracking-tight transition-colors',
              isDeleting
                ? 'text-red-600 dark:text-red-400'
                : 'text-slate-950 group-hover:text-emerald-700 dark:text-slate-100 dark:group-hover:text-emerald-300'
            )}
          >
            {isDeleting ? 'Delete workspace?' : (name || 'Untitled Workspace')}
          </h3>
        </div>

        {!isDeleting ? (
          <div className="relative z-10 flex items-center gap-0.5">
            {showExportJson && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onExportJson(id);
                }}
                disabled={exportJsonLoading}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200 disabled:pointer-events-none disabled:opacity-40"
                title="Download workspace JSON (metadata)"
              >
                <Download size={14} strokeWidth={2.5} />
              </button>
            )}
            <button
              onClick={handleDeleteClick}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500 dark:hover:border-red-900/50 dark:hover:bg-red-950/30 dark:hover:text-red-300"
              title="Delete Workspace"
            >
              <Trash2 size={15} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <div className="relative z-10 flex items-center gap-1 rounded-xl border border-red-200 bg-red-50/80 p-1 animate-in zoom-in-95 duration-200 dark:border-red-900/40 dark:bg-red-900/20">
            <button
              onClick={cancelDelete}
              className="p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={14} />
            </button>
            <button
              onClick={confirmDelete}
              className="rounded-lg bg-red-500 p-1.5 text-white shadow-sm transition-all hover:bg-red-600"
            >
              <Check size={14} />
            </button>
          </div>
        )}
      </div>
      {isLocked && (
        <div className="relative z-10 mb-3 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
          Upgrade required to access BigQuery workspace data
        </div>
      )}

      <div className="relative z-10 flex-1">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <Database size={12} strokeWidth={2.75} />
            {sources?.length || 0} {sources?.length === 1 ? 'Source' : 'Sources'}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MessageSquare size={12} strokeWidth={2.75} />
            {chatsCount || 0} {chatsCount === 1 ? 'Chat' : 'Chats'}
          </span>
        </div>
      </div>

      <div className="relative z-10 flex w-full items-center justify-between gap-3 border-t border-slate-100 pt-3 dark:border-slate-800">
        <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
          <Clock size={12} strokeWidth={2.5} />
          <span className="text-xs font-medium">{formatCreatedAt(createdAt)}</span>
        </div>
        {!isDeleting && !isLocked ? (
          <span
            role="button"
            tabIndex={0}
            onClick={e => {
              e.stopPropagation();
              onOpenWorkspace(id);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onOpenWorkspace(id);
              }
            }}
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-slate-600 opacity-0 transition-all hover:border-emerald-300 hover:text-emerald-700 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-emerald-700 dark:hover:text-emerald-300 dark:focus-visible:ring-offset-slate-900"
          >
            Open
            <ArrowRight size={14} strokeWidth={2.75} />
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default WorkspaceCard;
